import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from 'react-query';
import axios from 'axios';
import { 
  ChatBubbleLeftRightIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const MessageList = ({ onSelectConversation, filterType = 'all' }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Récupérer les conversations
  const { data: conversationsData } = useQuery(
    'conversations',
    async () => {
      const response = await axios.get('/api/messages/conversations');
      return response.data;
    },
    {
      enabled: !!user,
      refetchInterval: 10000 // Rafraîchir toutes les 10 secondes
    }
  );

  useEffect(() => {
    if (conversationsData) {
      let filteredConversations = conversationsData;
      
      // Appliquer le filtre selon le type
      switch (filterType) {
        case 'unread':
          filteredConversations = conversationsData.filter(conv => conv.unreadCount > 0);
          break;
        case 'archived':
          filteredConversations = conversationsData.filter(conv => conv.archived);
          break;
        default:
          filteredConversations = conversationsData.filter(conv => !conv.archived);
          break;
      }
      
      setConversations(filteredConversations);
      setLoading(false);
    }
  }, [conversationsData, filterType]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('fr-FR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit' 
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-text flex items-center">
          <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
          Messages
        </h2>
      </div>
      
      <div className="overflow-y-auto h-full">
        {conversations.length === 0 ? (
          <div className="p-6 text-center">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucune conversation</p>
            <p className="text-sm text-gray-500">Commencez une conversation avec un autre utilisateur</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => onSelectConversation(conversation)}
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {conversation.otherUser.avatar ? (
                      <img
                        className="h-10 w-10 rounded-full"
                        src={conversation.otherUser.avatar}
                        alt={conversation.otherUser.firstName}
                      />
                    ) : (
                      <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-600">
                          {conversation.otherUser.firstName[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-text truncate">
                        {conversation.otherUser.firstName} {conversation.otherUser.lastName}
                      </p>
                      <div className="flex items-center text-xs text-gray-500">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {formatTime(conversation.lastMessage.createdAt)}
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 truncate mt-1">
                      {conversation.lastMessage.content}
                    </p>
                    
                    {conversation.unreadCount > 0 && (
                      <div className="flex justify-end mt-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          {conversation.unreadCount}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageList;
